// src/components/LiveProof/index.js
import React, { useRef, useState, useEffect } from 'react';
import { PROOF_SPECS } from '../../proofSpecs';
import './LiveProof.css';

/**
 * Resolves a layer's `field` key to the matching dealership value.
 */
function resolveField(field, dealership) {
  if (!dealership || !field) return '';
  switch (field) {
    case 'name':         return (dealership.name || '').toUpperCase();
    case 'salesPhone':   return dealership.salesPhone || '';
    case 'servicePhone': return dealership.servicePhone || '';
    case 'email':        return dealership.email || '';
    case 'website':      return dealership.url || '';
    case 'city':         return dealership.city || '';
    case 'address': {
      const parts = [
        dealership.streetAddress,
        dealership.city,
        dealership.state,
        dealership.zip,
      ].filter(Boolean);
      return parts.join(', ');
    }
    default: {
      const value = field.split('.').reduce((obj, key) => obj?.[key], dealership);
      return value != null ? String(value) : '';
    }
  }
}

/**
 * Builds the inline style for a text overlay span.
 *
 * When coverArea is defined the span matches the cover rect exactly and
 * flexbox-centers the text inside it, so the text always sits within the
 * painted cover rectangle.
 *
 * When there is no coverArea, the original top/left + optional inline
 * background behaviour is used.
 */
function buildLayerStyle(layer, containerWidth) {
  const fontSize = Math.round(layer.fontSize * containerWidth);

  const fontFamily = layer.fontFamily || 'CadillacGothic-NarrowRegular, MyFont, sans-serif';

  if (layer.coverArea) {
    const justifyContent =
      layer.textAlign === 'left'  ? 'flex-start' :
      layer.textAlign === 'right' ? 'flex-end'   : 'center';

    return {
      position:       'absolute',
      top:            layer.coverArea.top,
      left:           layer.coverArea.left,
      width:          layer.coverArea.width,
      height:         layer.coverArea.height,
      display:        'flex',
      alignItems:     'center',
      justifyContent,
      fontFamily,
      fontSize:       `${fontSize}px`,
      color:          layer.color || '#000000',
      lineHeight:     1.2,
      padding:        '0 6px',
      overflow:       'hidden',
      pointerEvents:  'none',
    };
  }

  // No coverArea — original behaviour
  const style = {
    position:      'absolute',
    top:           layer.top,
    left:          layer.left,
    fontFamily,
    fontSize:      `${fontSize}px`,
    color:         layer.color || '#000000',
    textAlign:     layer.textAlign || 'left',
    transform:     layer.transform || undefined,
    whiteSpace:    'nowrap',
    lineHeight:    1.2,
    pointerEvents: 'none',
  };

  if (layer.coverBg) {
    const px = layer.paddingX ?? 4;
    const py = layer.paddingY ?? 2;
    style.backgroundColor = layer.coverBg;
    style.padding = `${py}px ${px}px`;
  }

  return style;
}

/**
 * LiveProof — renders a product image with dealership data overlaid as text layers.
 *
 * Props:
 *   product      {object}  — product object with .image
 *   dealership   {object}  — dealership object from DealershipContext
 *   productId    {string}  — used to look up the spec in PROOF_SPECS
 *   specOverride {object}  — when provided, bypasses PROOF_SPECS lookup (used by ProofBuilder)
 *
 * Returns null when no spec is found (caller falls back to a static <img>).
 *
 * Layer coverArea shape (optional):
 *   { top: '40%', left: '30%', width: '40%', height: '15%' }
 *   When present, a positioned <div> with coverBg color is rendered behind the text span.
 */
const LiveProof = ({ product, dealership, productId, specOverride, variantImage }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const spec = specOverride ?? PROOF_SPECS[String(productId)];

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (!spec) return null;

  const baseImage = spec.baseImage || variantImage || product?.image;

  return (
    <div
      className="live-proof-container"
      ref={containerRef}
      style={{ aspectRatio: spec.aspectRatio || 'auto' }}
    >
      {baseImage && (
        <img
          src={baseImage}
          alt={product?.name || 'Product'}
          className="live-proof-base"
        />
      )}

      {containerWidth > 0 && spec.layers?.map((layer, i) => (
        <React.Fragment key={i}>
          {/* Explicit cover-area rectangle, rendered behind the text */}
          {layer.coverArea && layer.coverBg && (
            <div
              className="live-proof-cover"
              style={{
                position: 'absolute',
                top: layer.coverArea.top,
                left: layer.coverArea.left,
                width: layer.coverArea.width,
                height: layer.coverArea.height,
                backgroundColor: layer.coverBg,
                pointerEvents: 'none',
              }}
            />
          )}
          <span
            className="live-proof-layer"
            style={buildLayerStyle(layer, containerWidth)}
          >
            {resolveField(layer.field, dealership)}{layer.suffix || ''}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

export default LiveProof;
export { resolveField };
